import { z } from 'zod';
/**
 * Alert types — Phase 5a expands the original 6 with rule-engine-
 * driven types from `apps/api/src/rules/predictive-maintenance/`.
 *
 * Add new types when shipping new rules; the rule registration site
 * is the source of truth for the rule_id ↔ alert type mapping.
 */
export const AlertTypeSchema = z.enum([
    // Pre-Phase-5 reserved enum (kept for back-compat — no rule fires these today)
    'charger_offline', 'connector_fault', 'high_temperature',
    'payment_failed', 'firmware_update', 'low_utilization',
    // Phase 5a rule-engine alert types (apps/api/src/rules/predictive-maintenance/)
    'pm.charger_offline',
    'pm.recurring_fault',
    'pm.stuck_connector',
    'pm.failed_transactions',
    'pm.power_anomaly',
    'pm.sim_on_air_ocpp_silent',
    'pm.sim_offline',
    // Standalone sweeps (apps/api/src/lib/*Sweep.ts) that write Alert rows
    // outside the rule engine. rule_id 'orphan_charging_profile'.
    'charger_current_capped',
    // rule_id 'refused_starts' (apps/api/src/lib/refusedStartSweep.ts): one
    // charger refused 3 or more app or RFID starts within 30 minutes.
    'charger_refusing_starts',
    // rule_id 'grandfather_fee_mismatch' (packages/billing hostSettlementLookup.ts): a grandfathered charger's fee
    // disagreed with its contract at a start; the session pinned the contract fee.
    'grandfather_fee_mismatch',
    // rule_id 'tariff_fallback' (apps/api/src/lib/tariffFallbackSweep.ts): an active public charger resolves no
    // tariff and would bill the fallback rate.
    'tariff_fallback',
]);
export const AlertSeveritySchema = z.enum(['info', 'warning', 'critical']);
/**
 * Phase 5a Item 7 — alert lifecycle state machine.
 *
 *   detected → (notification dispatched) → notified
 *   notified → (operator ack OR agent auto-action) → action_pending
 *   action_pending → (resolution predicate fires) → resolved
 *   action_pending → (escalation cadence elapses) → escalated
 *   escalated → super-admin notified; alert stays open
 *   ANY → resolution predicate fires → resolved
 */
export const AlertStatusSchema = z.enum([
    'detected', 'notified', 'action_pending', 'resolved', 'escalated',
]);
export const AlertSchema = z.object({
    _id: z.string(),
    tenant_id: z.string(),
    charger_id: z.string(),
    rule_id: z.string(),
    type: AlertTypeSchema,
    severity: AlertSeveritySchema,
    status: AlertStatusSchema,
    message: z.string(),
    // Phase 5a — replaces the old boolean acknowledged/resolved pair.
    // Timestamps are the source of truth for state transitions; status
    // is the denormalized "current state" field for fast filtering.
    detected_at: z.coerce.date(),
    notified_at: z.coerce.date().optional().nullable(),
    acknowledged_at: z.coerce.date().optional().nullable(),
    acknowledged_by: z.string().optional().nullable(),
    action_pending_at: z.coerce.date().optional().nullable(),
    escalated_at: z.coerce.date().optional().nullable(),
    resolved_at: z.coerce.date().optional().nullable(),
    // Pre-5a fields kept for back-compat with the `packages/shared`
    // contract — derived from the timestamps above on read.
    acknowledged: z.boolean(),
    resolved: z.boolean(),
    // New on 5a — structured payloads for rule context and resolution.
    trigger_payload: z.record(z.string(), z.unknown()),
    resolution_payload: z.record(z.string(), z.unknown()).optional(),
    // Audience — User IDs the notification fans out to. Computed at
    // detection time by `resolveAlertAudience` (apps/api/src/lib/alertAudience.ts).
    audience_user_ids: z.array(z.string()),
    // Track which escalation stages have fired (day_0/2/5/10 for warning,
    // day_0/1/3/7 for critical). Keyed by stage name to a boolean.
    escalation_stages_sent: z.record(z.string(), z.boolean()).optional(),
    // Refs to AgentActionLog rows produced for this alert.
    agent_actions_taken: z.array(z.string()).optional(),
    // Legacy `details` field — kept; treat as a synonym for
    // `trigger_payload` for the next-but-one Phase that retires it.
    details: z.record(z.string(), z.unknown()),
    created_at: z.coerce.date(),
});
//# sourceMappingURL=alert.js.map